/**
 * Cutscenes, the rules side (docs/archive/OPENING.md section 4): which scene is playing and its clock, the
 * triggers that start one, waiting for the player's answer, skipping, and what a scene leaves behind
 * when it ends. Scene data is data/scenes.ts; the client draws a scene as a function of (data, t).
 *
 * While a scene plays the World ignores every command but these, and the player cannot be struck
 * (world.ts asks `active`). The rest of the world runs on. Time only moves in `step`; nothing here is
 * random. Watching a scene to its end and skipping it apply the same `end`, so they leave the same
 * state. The playing scene is not saved: a reload starts it again from the beginning.
 */
import {
  ALL_SCENES as SCENE_LIST,
  CHAPTERS,
  SCENES,
  REPLAYABLE_SCENES,
  type ChapterDef,
  type Cue,
  type SceneDef,
  type Spot,
} from './data/scenes';
import { QUESTS } from './data/quests';
import { plains256 } from './data/zones';
import type { GameMap, Point } from './map';
import { freeAt, nearestFloor } from './path';
import type { PlayerProgress } from './progress';

/** The scene under way: its id, its clock (seconds, frozen while waiting) and whether it waits. */
export interface SceneState {
  readonly id: string;
  t: number;
  waiting: boolean;
}

/** Player commands of cutscenes (part of `PlayerCommand`). */
export type SceneCommand =
  /** Close the current line early, or answer a wait (a click, Space). */
  | { t: 'sceneAdvance' }
  /** End the scene now, as if watched to the end. */
  | { t: 'sceneSkip' }
  /** Watch a scene again (the legend, from the help window); only one already seen. */
  | { t: 'sceneReplay'; id: string };

/** World events of cutscenes (part of `WorldEvent`). */
export type SceneEvent = { type: 'scene'; id: string; phase: 'start' | 'end'; skipped?: boolean };

/** What the World lends the scene runner. */
export interface SceneHost {
  map(): GameMap;
  progress(): PlayerProgress;
  player(): { readonly x: number; readonly y: number; readonly dead: boolean };
  elder(): Point;
  /** Sets the player (and the pet beside them) down at a point. */
  place(x: number, y: number): void;
  /** Stops whatever the player was doing: walking, fighting, auto-battle, meditating. */
  hold(): void;
  /** Opens the elder's dialog as if the player had spoken to him. */
  talkElder(): void;
  notice(text: string): void;
  emit(e: SceneEvent): void;
}

/** Within this many tiles of a "go here" marker, it is reached and goes away. */
export const GUIDE_REACHED = 3;

const isPlayerActor = (d: SceneDef, actor: string) => d.actors[actor]?.sprite === 'player';

/** True for a character that has done nothing yet (it gets the opening). */
export function isNewCharacter(p: PlayerProgress): boolean {
  return (
    p.scenes.length === 0 &&
    p.quest.index === 0 &&
    p.quest.status === 'available' &&
    Object.keys(p.kills).length === 0
  );
}

/**
 * Whether a chapter of the story is done: its quest handed in (its legend chapter is open then). Its
 * closing scene plays where the chapter ends, but a chapter handed in elsewhere is done all the same.
 */
export function chapterDone(p: PlayerProgress, ch: ChapterDef): boolean {
  const k = QUESTS.findIndex((q) => q.id === ch.quest);
  return k >= 0 && p.quest.index > k;
}

/** The chapter the player is in: the first not yet done (the last one once all are). */
export function currentChapter(p: PlayerProgress): ChapterDef {
  return CHAPTERS.find((c) => !chapterDone(p, c)) ?? CHAPTERS[CHAPTERS.length - 1]!;
}

export class SceneRunner {
  /**
   * Whether the story's scenes run at all: on in the game (the client, a server), off in bare
   * simulations, which then never stop for a scene.
   */
  enabled = false;
  /** The scene under way, or null. */
  state: SceneState | null = null;
  /** A "go here" marker for the client (an arrow and a dot on the minimap), or null. */
  guide: Point | null = null;
  /** Where the player stood when the scene began ('player' spots). */
  private origin: Point = { x: 0, y: 0 };
  /** The time of the last wait answered: waits up to it are behind us. */
  private waitDone = -1;
  /** Watching a seen scene again: nothing it would leave behind is applied. */
  private replay = false;

  constructor(private readonly host: SceneHost) {}

  get active(): boolean {
    return this.state !== null;
  }

  get def(): SceneDef | undefined {
    return this.state ? SCENES[this.state.id] : undefined;
  }

  /** On entering the world: a new character's opening, or the next scene of a chain left unfinished. */
  load(): void {
    if (!this.enabled) return;
    const pr = this.host.progress();
    if (isNewCharacter(pr)) {
      const first = SCENE_LIST.find((d) => d.trigger.on === 'newCharacter' && this.can(d));
      if (first) return this.start(first.id);
    }
    const next = SCENE_LIST.find(
      (d) => d.trigger.on === 'after' && pr.scenes.includes(d.trigger.scene) && this.can(d),
    );
    if (next) this.start(next.id);
  }

  command(cmd: SceneCommand): void {
    if (cmd.t === 'sceneAdvance') this.advance();
    else if (cmd.t === 'sceneSkip') this.skip();
    else this.replayScene(cmd.id);
  }

  step(dt: number): void {
    const s = this.state;
    if (!s) {
      if (!this.enabled) return;
      this.checkNear();
      this.checkGuide();
      return;
    }
    if (s.waiting) return;
    this.runTo(s.t + dt);
  }

  /** Hands in a quest: the scene that closes it starts (chapter one's closing scene). */
  questDone(quest: string): void {
    if (this.state || !this.enabled) return;
    const d = SCENE_LIST.find(
      (x) => x.trigger.on === 'questDone' && x.trigger.quest === quest && this.can(x),
    );
    if (d) this.start(d.id);
  }

  /** Speaking to the elder: his first meeting plays instead of his dialog, if it is still to come. */
  meetElder(): boolean {
    if (this.state || !this.enabled) return false;
    const d = SCENE_LIST.find((x) => x.trigger.on === 'near' && x.trigger.spot.at === 'elder' && this.can(x));
    if (!d) return false;
    this.start(d.id);
    return true;
  }

  /** A spot on this map as a point on open ground. */
  point(spot: Spot): Point {
    const m = this.host.map();
    const base =
      spot.at === 'plaza'
        ? m.plaza
        : spot.at === 'elder'
          ? m.elder
          : spot.at === 'spawn'
            ? m.spawn
            : spot.at === 'player'
              ? this.origin
              : (m.landmarks?.[spot.at] ?? this.origin);
    const x = base.x + (spot.dx ?? 0);
    const y = base.y + (spot.dy ?? 0);
    if (freeAt(m, x, y)) return { x, y };
    const alt = nearestFloor(m, x, y, 3);
    return alt ? { x: alt[0], y: alt[1] } : { x: base.x, y: base.y };
  }

  // ───────────────────────── internals ─────────────────────────

  /** Whether a scene may start now: not seen yet, on its zone's map, at its point in the story. */
  private can(d: SceneDef): boolean {
    const pr = this.host.progress();
    if (pr.scenes.includes(d.id)) return false;
    if (d.zone && d.zone !== this.host.map().zone) return false;
    if (d.bigPlains && !plains256()) return false;
    if (d.beforeQuest != null && pr.quest.index >= d.beforeQuest) return false;
    if (d.offerOpen && pr.quest.status !== 'available') return false;
    return true;
  }

  private start(id: string, replay = false): void {
    const d = SCENES[id];
    if (!d) return;
    this.state = { id, t: 0, waiting: false };
    this.waitDone = -1;
    this.replay = replay;
    const p = this.host.player();
    this.origin = { x: p.x, y: p.y };
    this.host.hold();
    this.host.emit({ type: 'scene', id, phase: 'start' });
    // Cues at the very start (setting the player down) apply at once.
    this.playerCues(d, -1, 0);
  }

  /** Runs the clock forward to `t1`, stopping at a wait not yet answered and ending at the end. */
  private runTo(t1: number): void {
    const s = this.state!;
    const d = SCENES[s.id]!;
    const t0 = s.t;
    const wait = d.cues.find((c) => c.do === 'wait' && c.at > this.waitDone && c.at >= t0 && c.at <= t1);
    if (wait) {
      this.playerCues(d, t0, wait.at);
      s.t = wait.at;
      s.waiting = true;
      return;
    }
    this.playerCues(d, t0, t1);
    s.t = Math.min(t1, d.length);
    if (t1 >= d.length) this.finish(false);
  }

  /** The World's share of the cues: the player set down where a 'place' or a 'walk' leaves them. */
  private playerCues(d: SceneDef, t0: number, t1: number): void {
    if (this.replay) return;
    for (const c of d.cues) {
      const when = c.do === 'place' ? c.at : c.do === 'walk' ? c.at + c.dur : null;
      if (when == null || when <= t0 || when > t1) continue;
      const actor = (c as Extract<Cue, { actor: string }>).actor;
      if (!isPlayerActor(d, actor)) continue;
      const spot = c.do === 'place' ? c.spot : c.do === 'walk' ? c.to : null;
      if (!spot) continue;
      const at = this.point(spot);
      this.host.place(at.x, at.y);
    }
  }

  private advance(): void {
    const s = this.state;
    if (!s) return;
    const d = SCENES[s.id]!;
    if (s.waiting) {
      s.waiting = false;
      this.waitDone = s.t;
      return;
    }
    // A line being spoken closes; on the panels, the next panel comes.
    const line = d.cues.find((c) => c.do === 'say' && c.at <= s.t && s.t < c.at + c.dur);
    if (line && line.do === 'say') return this.runTo(line.at + line.dur);
    if (d.kind === 'panels') {
      const next = d.cues.find((c) => c.do === 'panel' && c.at > s.t + 1e-6);
      if (next) this.runTo(next.at);
    }
  }

  private skip(): void {
    const s = this.state;
    if (!s) return;
    const d = SCENES[s.id]!;
    this.playerCues(d, s.t, Infinity);
    s.t = d.length;
    this.finish(true);
  }

  private replayScene(id: string): void {
    const pr = this.host.progress();
    if (this.state || !this.enabled || !REPLAYABLE_SCENES.includes(id) || !pr.scenes.includes(id)) return;
    if (this.host.player().dead) return;
    this.start(id, true);
  }

  private finish(skipped: boolean): void {
    const d = this.def!;
    const replay = this.replay;
    this.state = null;
    this.replay = false;
    const pr = this.host.progress();
    if (!replay) {
      if (!pr.scenes.includes(d.id)) pr.scenes.push(d.id);
      const e = d.end;
      if (e.placePlayer) {
        const at = this.point(e.placePlayer);
        this.host.place(at.x, at.y);
      }
    }
    this.host.emit({ type: 'scene', id: d.id, phase: 'end', ...(skipped ? { skipped } : {}) });
    if (replay) {
      // Watching again plays on through the next scene that can be watched again.
      const next = SCENE_LIST.find(
        (x) =>
          x.trigger.on === 'after' &&
          x.trigger.scene === d.id &&
          REPLAYABLE_SCENES.includes(x.id) &&
          pr.scenes.includes(x.id),
      );
      if (next) this.start(next.id, true);
      return;
    }
    const e = d.end;
    if (e.notice) this.host.notice(e.notice);
    if (e.guideTo) {
      const at = e.guideTo === 'elder' ? this.host.elder() : this.point(e.guideTo);
      this.guide = { x: at.x, y: at.y };
    }
    const next = SCENE_LIST.find((x) => x.trigger.on === 'after' && x.trigger.scene === d.id && this.can(x));
    if (next) return this.start(next.id);
    if (e.talk === 'elder') this.host.talkElder();
  }

  /** Walking near a spot starts its scene. */
  private checkNear(): void {
    const p = this.host.player();
    if (p.dead) return;
    for (const d of SCENE_LIST) {
      if (d.trigger.on !== 'near' || !this.can(d)) continue;
      const at = d.trigger.spot.at === 'elder' ? this.host.elder() : this.point(d.trigger.spot);
      if (Math.hypot(p.x - at.x, p.y - at.y) < d.trigger.r) return this.start(d.id);
    }
  }

  private checkGuide(): void {
    const g = this.guide;
    if (!g) return;
    const p = this.host.player();
    if (Math.hypot(p.x - g.x, p.y - g.y) < GUIDE_REACHED) this.guide = null;
  }
}
